import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import omit from 'lodash/fp/omit';
import mapValues from 'lodash/mapValues';
import isString from 'lodash/isString';
import { useIntl } from 'react-intl';
import { ClassNames, css } from '@emotion/react';
import { reaction } from 'mobx';
import useStores from 'hooks/use-stores.hooks';
import { observer } from 'mobx-react';
import { notification as antdNotification } from 'antd';
import { isTranslationId } from 'components/localization';
import type { NotificationArgs } from 'types/notifications.types';

/**
 * `className` lands on `.ant-notification-notice`, where antd declares `width` and `max-width` - so
 * those two need doubling. `white-space` it never declares.
 */
const noticeCss = css`
  white-space: pre-line;

  && {
    width: fit-content;
    max-width: 500px;
  }
`;

type EmitterProps = { noticeClassName: string };

// error toasts never close on their own, and each api failure now carries its own request context, so
// dedup no longer collapses a burst of them - antd drops the oldest past this many
const MAX_VISIBLE_NOTIFICATIONS = 3;

const Emitter: React.FunctionComponent<EmitterProps> = ({ noticeClassName }) => {
  const { notificationStore } = useStores();
  const [notificationsApi, contextHolder] = antdNotification.useNotification({
    maxCount: MAX_VISIBLE_NOTIFICATIONS
  });

  // the resolved text, not the keys - two failures share `api.error.request-context` but differ once formatted
  const lastNotification = useRef<{ message: React.ReactNode; description?: string } | null>(null);

  const intl = useIntl();

  const onNotificationClose = useCallback(() => (lastNotification.current = null), []);

  const showNotification = useCallback(
    (notification: NotificationArgs | null) => {
      if (!notification?.message) {
        return;
      }

      // a variable can itself be a translation key (e.g. the resource name of a failed API call)
      const messageVariables =
        notification.messageVariables &&
        mapValues(notification.messageVariables, value =>
          isString(value) && isTranslationId(value) ? intl.formatMessage({ id: value }) : value
        );

      // api failures arrive here as the server's own English text, not as a translation key, and a caller
      // with links in its toast passes an element
      const message =
        isString(notification.message) && isTranslationId(notification.message)
          ? intl.formatMessage({ id: notification.message }, messageVariables)
          : notification.message;

      const description =
        notification.description && isTranslationId(notification.description)
          ? intl.formatMessage({ id: notification.description }, notification.descriptionVariables)
          : notification.description;

      if (lastNotification.current?.message !== message || lastNotification.current?.description !== description) {
        // only way to get an html identifier on the notification
        const e2eClassName = `e2e-${notification.notificationType}-notification`;

        notificationsApi[notification.notificationType]({
          ...omit(['notificationType', 'messageVariables', 'descriptionVariables'], notification),
          message,
          description,
          className: `${e2eClassName} ${noticeClassName}`,
          onClose: onNotificationClose,
          duration: notification.notificationType === 'error' ? 0 : 5
        });

        lastNotification.current = { message, description };
      }
    },
    [notificationsApi, intl, onNotificationClose, noticeClassName]
  );

  // A reaction, not an effect on the rendered value: React batches two sets in one tick into one render, so
  // an effect would see only the second - an api failure's toast and its caller's own, say.
  useEffect(
    () => reaction(() => notificationStore.notification, showNotification, { fireImmediately: true }),
    [notificationStore, showNotification]
  );

  return contextHolder;
};

/**
 * The class has to be a string before the effect can hand it to antd's imperative API, and
 * `ClassNames` is a render prop - hence the split from the emitter above.
 */
const NotificationManager: React.FunctionComponent = () => {
  return <ClassNames>{({ css: toClassName }) => <Emitter noticeClassName={toClassName(noticeCss)} />}</ClassNames>;
};

export default observer(NotificationManager);
